import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import Logo from '../components/Logo'
import CodeEditor from '../components/CodeEditor'
import { useAuth } from '../contexts/AuthContext'
import { memoryApi } from '../utils/api'

type AdminTab = 'overview' | 'tokens' | 'submissions' | 'settings'

export default function AdminQecPage() {
  const { user } = useAuth()
  const isAdmin = !!user?.is_admin

  // Challenge list & selection
  const [memories, setMemories] = useState<any[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<any>(null)
  const [activeTab, setActiveTab] = useState<AdminTab>('overview')
  const [isLoading, setIsLoading] = useState(true)

  // Create form
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState({
    title: '', description: '', ibmq_api_key: '',
    allowed_backends: 'ibm_torino, ibm_fez, ibm_boston, ibm_pittsburgh, ibm_kingston, ibm_marrakesh, ibm_miami, ibm_brisbane',
    noise_levels: '0.0005, 0.001, 0.005',
    max_shots: 100000, max_qec_rounds: 50, total_budget_seconds: 21600,
    num_participants: 50, max_concurrent_jobs: 3, starter_code: '',
  })
  const [isCreating, setIsCreating] = useState(false)

  // Tokens
  const [tokenUids, setTokenUids] = useState('')
  const [generatedTokens, setGeneratedTokens] = useState<any[]>([])
  const [isGenerating, setIsGenerating] = useState(false)

  // Submissions
  const [submissions, setSubmissions] = useState<any[]>([])
  const [subsTotal, setSubsTotal] = useState(0)
  const [subsPage, setSubsPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const [isLoadingSubs, setIsLoadingSubs] = useState(false)
  const [expandedSub, setExpandedSub] = useState<string | null>(null)

  // Settings
  const [settingsForm, setSettingsForm] = useState({ title: '', description: '', is_active: true, starter_code: '', allowed_backends: '', noise_levels: '', max_shots: 100000, max_qec_rounds: 50 })
  const [isSaving, setIsSaving] = useState(false)
  const [saveMsg, setSaveMsg] = useState('')

  // Fetch memories
  useEffect(() => {
    memoryApi.adminList()
      .then(r => { setMemories(r.memories); if (r.memories.length > 0 && !selectedId) setSelectedId(r.memories[0].id) })
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }, [])

  // Fetch detail when selection changes
  const fetchDetail = useCallback((id: string) => {
    memoryApi.adminGetDetail(id).then(d => {
      setDetail(d)
      setSettingsForm({
        title: d.title || '', description: d.description || '', is_active: d.is_active,
        starter_code: d.starter_code || '',
        allowed_backends: (d.allowed_backends || []).join(', '),
        noise_levels: (d.noise_levels || []).join(', '),
        max_shots: d.max_shots || 100000, max_qec_rounds: d.max_qec_rounds || 50,
      })
    }).catch(() => {})
  }, [])

  useEffect(() => { if (selectedId) fetchDetail(selectedId) }, [selectedId, fetchDetail])

  // Fetch submissions
  const fetchSubs = useCallback((id: string, page = 1, status = '') => {
    setIsLoadingSubs(true)
    memoryApi.adminGetSubmissions(id, page, 25, status || undefined)
      .then(r => { setSubmissions(r.submissions); setSubsTotal(r.total); setSubsPage(r.page) })
      .catch(() => {})
      .finally(() => setIsLoadingSubs(false))
  }, [])

  useEffect(() => { if (selectedId && activeTab === 'submissions') fetchSubs(selectedId, 1, statusFilter) }, [selectedId, activeTab, statusFilter, fetchSubs])

  // Handlers
  const handleCreate = async () => {
    setIsCreating(true)
    try {
      const r = await memoryApi.adminCreate({
        title: createForm.title, description: createForm.description,
        ibmq_api_key: createForm.ibmq_api_key,
        allowed_backends: createForm.allowed_backends.split(',').map(s => s.trim()).filter(Boolean),
        noise_levels: createForm.noise_levels.split(',').map(s => parseFloat(s.trim())).filter(n => !isNaN(n)),
        max_shots: createForm.max_shots, max_qec_rounds: createForm.max_qec_rounds,
        total_budget_seconds: createForm.total_budget_seconds,
        num_participants: createForm.num_participants, max_concurrent_jobs: createForm.max_concurrent_jobs,
        starter_code: createForm.starter_code || undefined,
      })
      setShowCreate(false)
      setSelectedId(r.id)
      memoryApi.adminList().then(r2 => setMemories(r2.memories))
    } catch (e: any) { alert(e.message || 'Create failed') }
    finally { setIsCreating(false) }
  }

  const handleGenerateTokens = async () => {
    if (!selectedId || !tokenUids.trim()) return
    setIsGenerating(true)
    try {
      const participants = tokenUids.split('\n').map(line => {
        const parts = line.trim().split(',').map(s => s.trim())
        return parts.length > 1 ? [parts[0], parts[1]] : [parts[0], parts[0]]
      }).filter(p => p[0])
      const r = await memoryApi.adminGenerateTokens(selectedId, participants)
      setGeneratedTokens(r.tokens)
    } catch (e: any) { alert(e.message || 'Token generation failed') }
    finally { setIsGenerating(false) }
  }

  const handleSaveSettings = async () => {
    if (!selectedId) return
    setIsSaving(true); setSaveMsg('')
    try {
      await memoryApi.adminUpdate(selectedId, {
        title: settingsForm.title, description: settingsForm.description,
        is_active: settingsForm.is_active, starter_code: settingsForm.starter_code,
        allowed_backends: settingsForm.allowed_backends.split(',').map(s => s.trim()).filter(Boolean),
        noise_levels: settingsForm.noise_levels.split(',').map(s => parseFloat(s.trim())).filter(n => !isNaN(n)),
        max_shots: settingsForm.max_shots, max_qec_rounds: settingsForm.max_qec_rounds,
      })
      setSaveMsg('Saved!')
      fetchDetail(selectedId)
    } catch (e: any) { setSaveMsg('Error: ' + (e.message || 'Failed')) }
    finally { setIsSaving(false) }
  }

  const handleDeleteSub = async (subId: string) => {
    if (!confirm('Delete this submission?')) return
    await memoryApi.adminDeleteSubmission(subId)
    if (selectedId) fetchSubs(selectedId, subsPage, statusFilter)
  }

  const handleClearStuck = async () => {
    if (!confirm('Clear all stuck (queued/running) jobs?')) return
    const r = await memoryApi.adminClearStuckJobs()
    alert(`Cleared ${r.cleared} stuck jobs`)
    if (selectedId) fetchSubs(selectedId, subsPage, statusFilter)
  }

  if (!isAdmin) {
    return <div className="min-h-screen flex items-center justify-center text-qcloud-muted">Admin access required</div>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-qcloud-border px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/"><Logo size="small" /></Link>
          <span className="text-lg font-bold text-qcloud-text">QEC Challenge Admin</span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <Link to="/admin/homework" className="text-qcloud-muted hover:text-qcloud-primary">Homework Admin</Link>
          <Link to="/admin/challenges" className="text-qcloud-muted hover:text-qcloud-primary">Challenge Admin</Link>
          <Link to="/" className="text-qcloud-muted hover:text-qcloud-primary">Home</Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Top: Create + Challenge selector */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-2 flex-wrap">
            {memories.map(m => (
              <button key={m.id} onClick={() => setSelectedId(m.id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${selectedId === m.id ? 'bg-qcloud-primary text-white' : 'bg-white border text-qcloud-text hover:border-qcloud-primary'}`}>
                {m.title} {!m.is_active && <span className="text-xs opacity-60">(inactive)</span>}
              </button>
            ))}
          </div>
          <button onClick={() => setShowCreate(!showCreate)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-500">
            {showCreate ? 'Cancel' : '+ New Challenge'}
          </button>
        </div>

        {/* Create Form */}
        {showCreate && (
          <div className="bg-white rounded-xl border p-6 mb-6 space-y-4">
            <h3 className="font-bold text-lg">Create New QEC Challenge</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-qcloud-muted block mb-1">Title</label>
                <input value={createForm.title} onChange={e => setCreateForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full px-3 py-2 border rounded text-sm" placeholder="Distance-3 Surface Code" />
              </div>
              <div>
                <label className="text-xs text-qcloud-muted block mb-1">IBM API Key</label>
                <input type="password" value={createForm.ibmq_api_key} onChange={e => setCreateForm(f => ({ ...f, ibmq_api_key: e.target.value }))}
                  className="w-full px-3 py-2 border rounded text-sm" placeholder="Your IBM Quantum API key" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-qcloud-muted block mb-1">Description (Markdown)</label>
                <textarea value={createForm.description} onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
                  rows={3} className="w-full px-3 py-2 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-qcloud-muted block mb-1">Backends (comma-separated)</label>
                <input value={createForm.allowed_backends} onChange={e => setCreateForm(f => ({ ...f, allowed_backends: e.target.value }))}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-qcloud-muted block mb-1">Noise Levels (comma-separated)</label>
                <input value={createForm.noise_levels} onChange={e => setCreateForm(f => ({ ...f, noise_levels: e.target.value }))}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-qcloud-muted block mb-1">Max Shots</label>
                <input type="number" value={createForm.max_shots} onChange={e => setCreateForm(f => ({ ...f, max_shots: Number(e.target.value) }))}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-qcloud-muted block mb-1">Budget (seconds total)</label>
                <input type="number" value={createForm.total_budget_seconds} onChange={e => setCreateForm(f => ({ ...f, total_budget_seconds: Number(e.target.value) }))}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
            </div>
            <button onClick={handleCreate} disabled={isCreating || !createForm.title || !createForm.ibmq_api_key}
              className="px-6 py-2 bg-qcloud-primary text-white rounded-lg disabled:opacity-50">
              {isCreating ? 'Creating...' : 'Create Challenge'}
            </button>
          </div>
        )}

        {/* Tabs */}
        {selectedId && (
          <>
            <div className="border-b border-qcloud-border mb-6 flex gap-1">
              {(['overview', 'tokens', 'submissions', 'settings'] as AdminTab[]).map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 capitalize ${activeTab === tab ? 'border-qcloud-primary text-qcloud-primary' : 'border-transparent text-qcloud-muted hover:text-qcloud-text'}`}>
                  {tab}
                </button>
              ))}
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && detail && (
              <div className="space-y-6">
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl border p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{detail.budget?.num_tokens || 0}</div>
                    <div className="text-xs text-qcloud-muted">Tokens</div>
                  </div>
                  <div className="bg-white rounded-xl border p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">{detail.budget?.active_tokens || 0}</div>
                    <div className="text-xs text-qcloud-muted">Active</div>
                  </div>
                  <div className="bg-white rounded-xl border p-4 text-center">
                    <div className="text-2xl font-bold text-purple-600">{detail.submissions?.total || 0}</div>
                    <div className="text-xs text-qcloud-muted">Submissions</div>
                  </div>
                  <div className="bg-white rounded-xl border p-4 text-center">
                    <div className="text-2xl font-bold text-amber-600">{Math.round((detail.budget?.total_used_seconds || 0) / 60)}m</div>
                    <div className="text-xs text-qcloud-muted">Budget Used</div>
                  </div>
                </div>
                {/* Status breakdown */}
                {detail.submissions?.by_status && Object.keys(detail.submissions.by_status).length > 0 && (
                  <div className="bg-white rounded-xl border p-4">
                    <h4 className="text-sm font-semibold mb-3">Submission Status</h4>
                    <div className="flex gap-4">
                      {Object.entries(detail.submissions.by_status as Record<string, number>).map(([status, count]) => (
                        <div key={status} className="flex items-center gap-2 text-sm">
                          <div className={`w-2.5 h-2.5 rounded-full ${status === 'completed' ? 'bg-green-500' : status === 'failed' ? 'bg-red-500' : status === 'running' ? 'bg-blue-500' : 'bg-amber-500'}`} />
                          <span className="capitalize">{status}: <strong>{count}</strong></span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Participants */}
                {detail.participants?.length > 0 && (
                  <div className="bg-white rounded-xl border overflow-hidden">
                    <h4 className="text-sm font-semibold p-4 border-b">Participants ({detail.participants.length})</h4>
                    <table className="w-full text-sm">
                      <thead><tr className="bg-gray-50 text-xs text-qcloud-muted uppercase">
                        <th className="px-4 py-2 text-left">Name</th>
                        <th className="px-4 py-2 text-left">Method</th>
                        <th className="px-4 py-2 text-right">Used</th>
                        <th className="px-4 py-2 text-right">Limit</th>
                        <th className="px-4 py-2 text-right">Submissions</th>
                        <th className="px-4 py-2 text-right">Active</th>
                      </tr></thead>
                      <tbody className="divide-y">
                        {detail.participants.map((p: any) => (
                          <tr key={p.token_id} className="hover:bg-gray-50">
                            <td className="px-4 py-2 font-medium">{p.display_name || '—'}</td>
                            <td className="px-4 py-2 text-qcloud-muted">{p.method_name || '—'}</td>
                            <td className="px-4 py-2 text-right font-mono">{Math.round(p.budget_used)}s</td>
                            <td className="px-4 py-2 text-right font-mono">{Math.round(p.budget_limit)}s</td>
                            <td className="px-4 py-2 text-right">{p.submission_count}</td>
                            <td className="px-4 py-2 text-right">{p.is_active ? <span className="text-green-600">Yes</span> : <span className="text-red-500">No</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Tokens Tab */}
            {activeTab === 'tokens' && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl border p-6">
                  <h3 className="font-semibold mb-3">Generate Tokens</h3>
                  <p className="text-xs text-qcloud-muted mb-2">One UID per line. Format: uid or uid, display_name</p>
                  <textarea value={tokenUids} onChange={e => setTokenUids(e.target.value)} rows={6}
                    className="w-full px-3 py-2 border rounded text-sm font-mono" placeholder="student1@ucla.edu, Alice&#10;student2@ucla.edu, Bob" />
                  <button onClick={handleGenerateTokens} disabled={isGenerating || !tokenUids.trim()}
                    className="mt-3 px-4 py-2 bg-qcloud-primary text-white rounded-lg text-sm disabled:opacity-50">
                    {isGenerating ? 'Generating...' : 'Generate Tokens'}
                  </button>
                </div>
                {generatedTokens.length > 0 && (
                  <div className="bg-white rounded-xl border overflow-hidden">
                    <div className="flex items-center justify-between p-4 border-b">
                      <h4 className="font-semibold">Generated Tokens ({generatedTokens.length})</h4>
                      <button onClick={() => {
                        const csv = generatedTokens.map(t => `${t.participant_uid},${t.token}`).join('\n')
                        navigator.clipboard.writeText(csv)
                      }} className="text-xs text-qcloud-primary hover:underline">Copy as CSV</button>
                    </div>
                    <table className="w-full text-sm">
                      <thead><tr className="bg-gray-50 text-xs text-qcloud-muted uppercase">
                        <th className="px-4 py-2 text-left">UID</th>
                        <th className="px-4 py-2 text-left">Display Name</th>
                        <th className="px-4 py-2 text-left">Token</th>
                      </tr></thead>
                      <tbody className="divide-y">
                        {generatedTokens.map((t, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-4 py-2">{t.participant_uid}</td>
                            <td className="px-4 py-2">{t.display_name || '—'}</td>
                            <td className="px-4 py-2 font-mono text-xs break-all">{t.token}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Submissions Tab */}
            {activeTab === 'submissions' && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  {['', 'queued', 'running', 'completed', 'failed'].map(s => (
                    <button key={s} onClick={() => setStatusFilter(s)}
                      className={`px-3 py-1 rounded text-xs font-medium ${statusFilter === s ? 'bg-qcloud-primary text-white' : 'bg-white border text-qcloud-muted hover:text-qcloud-text'}`}>
                      {s || 'All'}
                    </button>
                  ))}
                  <div className="flex-1" />
                  <button onClick={handleClearStuck} className="px-3 py-1 bg-red-50 text-red-600 border border-red-200 rounded text-xs hover:bg-red-100">
                    Clear Stuck Jobs
                  </button>
                  <span className="text-xs text-qcloud-muted">{subsTotal} total</span>
                </div>

                <div className="bg-white rounded-xl border overflow-hidden">
                  {isLoadingSubs ? (
                    <div className="text-center py-8 text-qcloud-muted">Loading...</div>
                  ) : submissions.length === 0 ? (
                    <div className="text-center py-8 text-qcloud-muted">No submissions</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead><tr className="bg-gray-50 text-xs text-qcloud-muted uppercase">
                        <th className="px-3 py-2 text-left">ID</th>
                        <th className="px-3 py-2 text-left">Participant</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-right">LER</th>
                        <th className="px-3 py-2 text-left">Backend</th>
                        <th className="px-3 py-2 text-left">IBM Job</th>
                        <th className="px-3 py-2 text-left">Created</th>
                        <th className="px-3 py-2"></th>
                      </tr></thead>
                      <tbody className="divide-y">
                        {submissions.map(s => (
                          <tr key={s.id} className="hover:bg-gray-50">
                            <td className="px-3 py-2">
                              <button onClick={() => setExpandedSub(expandedSub === s.id ? null : s.id)}
                                className="font-mono text-xs text-qcloud-primary hover:underline">{s.id.slice(0, 8)}</button>
                            </td>
                            <td className="px-3 py-2">{s.participant?.display_name || '—'}</td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                s.status === 'completed' ? 'bg-green-100 text-green-700' :
                                s.status === 'failed' ? 'bg-red-100 text-red-700' :
                                s.status === 'running' ? 'bg-blue-100 text-blue-700' :
                                'bg-amber-100 text-amber-700'
                              }`}>{s.status}</span>
                            </td>
                            <td className="px-3 py-2 text-right font-mono">
                              {s.logical_error_rate != null ? (s.logical_error_rate * 100).toFixed(3) + '%' : '—'}
                            </td>
                            <td className="px-3 py-2 text-xs">{s.backend_name}</td>
                            <td className="px-3 py-2 font-mono text-[10px]">{s.ibmq_job_id?.slice(0, 12) || '—'}</td>
                            <td className="px-3 py-2 text-xs text-qcloud-muted">{s.created_at?.slice(0, 16)}</td>
                            <td className="px-3 py-2">
                              <button onClick={() => handleDeleteSub(s.id)} className="text-red-400 hover:text-red-600 text-xs">Del</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Expanded detail */}
                {expandedSub && (() => {
                  const s = submissions.find(x => x.id === expandedSub)
                  if (!s) return null
                  return (
                    <div className="bg-white rounded-xl border p-4 space-y-2 text-xs">
                      <div className="flex justify-between"><strong>Submission ID:</strong> <span className="font-mono">{s.id}</span></div>
                      <div className="flex justify-between"><strong>Qubits / Gates / Depth:</strong> <span>{s.qubit_count} / {s.gate_count} / {s.circuit_depth}</span></div>
                      <div className="flex justify-between"><strong>Shots:</strong> <span>{s.shots}</span></div>
                      <div className="flex justify-between"><strong>QEC Rounds:</strong> <span>{s.qec_rounds}</span></div>
                      <div className="flex justify-between"><strong>Execution Time:</strong> <span>{s.execution_time_seconds}s</span></div>
                      {s.error_message && <div className="text-red-500"><strong>Error:</strong> {s.error_message}</div>}
                      {s.layout_mapping && <div><strong>Layout:</strong> <span className="font-mono text-[10px]">{s.layout_mapping}</span></div>}
                      {s.stim_code && <div><strong>Code (truncated):</strong> <pre className="mt-1 bg-gray-50 p-2 rounded text-[10px] overflow-x-auto">{s.stim_code}</pre></div>}
                    </div>
                  )
                })()}

                {/* Pagination */}
                {subsTotal > 25 && (
                  <div className="flex justify-center gap-2">
                    <button disabled={subsPage <= 1} onClick={() => selectedId && fetchSubs(selectedId, subsPage - 1, statusFilter)}
                      className="px-3 py-1 border rounded text-sm disabled:opacity-50">Prev</button>
                    <span className="text-sm text-qcloud-muted px-3 py-1">Page {subsPage} / {Math.ceil(subsTotal / 25)}</span>
                    <button disabled={subsPage >= Math.ceil(subsTotal / 25)} onClick={() => selectedId && fetchSubs(selectedId, subsPage + 1, statusFilter)}
                      className="px-3 py-1 border rounded text-sm disabled:opacity-50">Next</button>
                  </div>
                )}
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="bg-white rounded-xl border p-6 space-y-4 max-w-2xl">
                <div>
                  <label className="text-xs text-qcloud-muted block mb-1">Title</label>
                  <input value={settingsForm.title} onChange={e => setSettingsForm(f => ({ ...f, title: e.target.value }))}
                    className="w-full px-3 py-2 border rounded text-sm" />
                </div>
                <div>
                  <label className="text-xs text-qcloud-muted block mb-1">Description (Markdown)</label>
                  <textarea value={settingsForm.description} onChange={e => setSettingsForm(f => ({ ...f, description: e.target.value }))}
                    rows={4} className="w-full px-3 py-2 border rounded text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-qcloud-muted block mb-1">Backends (comma-separated)</label>
                    <input value={settingsForm.allowed_backends} onChange={e => setSettingsForm(f => ({ ...f, allowed_backends: e.target.value }))}
                      className="w-full px-3 py-2 border rounded text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-qcloud-muted block mb-1">Noise Levels</label>
                    <input value={settingsForm.noise_levels} onChange={e => setSettingsForm(f => ({ ...f, noise_levels: e.target.value }))}
                      className="w-full px-3 py-2 border rounded text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-qcloud-muted block mb-1">Max Shots</label>
                    <input type="number" value={settingsForm.max_shots} onChange={e => setSettingsForm(f => ({ ...f, max_shots: Number(e.target.value) }))}
                      className="w-full px-3 py-2 border rounded text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-qcloud-muted block mb-1">Max QEC Rounds</label>
                    <input type="number" value={settingsForm.max_qec_rounds} onChange={e => setSettingsForm(f => ({ ...f, max_qec_rounds: Number(e.target.value) }))}
                      className="w-full px-3 py-2 border rounded text-sm" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={settingsForm.is_active} onChange={e => setSettingsForm(f => ({ ...f, is_active: e.target.checked }))} />
                  <label className="text-sm">Active</label>
                </div>
                <div>
                  <label className="text-xs text-qcloud-muted block mb-1">Starter Code (QStabIR)</label>
                  <div className="h-64 border rounded overflow-hidden">
                    <CodeEditor value={settingsForm.starter_code} onChange={v => v !== undefined && setSettingsForm(f => ({ ...f, starter_code: v }))} />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={handleSaveSettings} disabled={isSaving}
                    className="px-6 py-2 bg-qcloud-primary text-white rounded-lg disabled:opacity-50">
                    {isSaving ? 'Saving...' : 'Save Settings'}
                  </button>
                  {saveMsg && <span className={`text-sm ${saveMsg.startsWith('Error') ? 'text-red-500' : 'text-green-600'}`}>{saveMsg}</span>}
                </div>
              </div>
            )}
          </>
        )}

        {isLoading && <div className="text-center py-12 text-qcloud-muted">Loading...</div>}
        {!isLoading && memories.length === 0 && !showCreate && (
          <div className="text-center py-12 text-qcloud-muted">No QEC challenges yet. Click "+ New Challenge" to create one.</div>
        )}
      </div>
    </div>
  )
}
